import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import {
    autoinject,
    bind,
    displayFormatTime,
    inject,
    ModalHandle,
    ServicesAlert,
    SoftwareUpdateAlert,
    toTimezoneAndFormat,
} from 'Mri.Web.Common';
import * as React from 'react';
import { LinkContainer } from 'react-router-bootstrap';
import { ButtonGroup, DropdownItem, DropdownMenu, DropdownToggle, UncontrolledButtonDropdown } from 'reactstrap';
import { AccountStore } from '../Account/AccountStore';
import { ContractsStore } from '../Contracts/ContractsStore';
import { AppStore } from '../Core/AppStore';
import { FavoriteButton } from '../Core/FavoriteButton';
import { RdpLink } from '../Core/RdpLink';
import { ActiveDisplay } from '../Displays/ActiveDisplay';
import { DisplayEditModal } from './DisplayView/Settings/DisplayEditModal';
import { DisplayEventsModal } from './DisplayEventsModal';
import { DisplaysStore } from './DisplaysStore';
import { TerminalModal } from './TerminalModal';
import { TicketModal } from './TicketModal';
import { TicketsModal } from './TicketsModal';
import config from 'config';
import {ContractModal} from "../Contracts/ContractModal";

export interface IMapDisplayItemProps {
    onClick?(): void;
    readonly display: ActiveDisplay;
    readonly account?: Mri.Accounts.Account;
    readonly history: any;
}

@autoinject
@observer
export class MapDisplayItem extends React.Component<IMapDisplayItemProps> {
    public render() {
        const { display, account } = this.props;
        return (
            <div onClick={this.props.onClick} className={this.hasLatAndLng ? 'map-display-item w-100' : 'w-100'}>
                <div className={this.statusClassName + ' rounded mb-1 col-12 p-2'}>
                    <div className="row">
                        <div className="col-6">
                            <div className="flex-items-center">
                                <ButtonGroup className="w-100">
                                    <LinkContainer to={`/displays/${display.serialNumber}`}>
                                        <a className="btn btn-outline-white btn-sm text-white p-1">
                                            {display.serialNumber}
                                        </a>
                                    </LinkContainer>
                                    <UncontrolledButtonDropdown>
                                        <DropdownToggle
                                            caret
                                            color="white"
                                            className="btn btn-outline-white p-1 pe-2"
                                            size="sm"
                                        />
                                        <DropdownMenu>
                                            <DropdownItem
                                                tag="a"
                                                href={`${this.accountStore.portalUrl}/proxy/${display.serialNumber}`}
                                                target={this.accountStore.user?.statusVuNewTab ? '_blank' : ''}
                                                className={this.disabledClass}
                                            >
                                                StatusVu&reg;
                                            </DropdownItem>

                                            <DropdownItem onClick={this.openCreateTicketModal}>
                                                Create Ticket
                                            </DropdownItem>

                                            <DropdownItem onClick={this.openDisplayEditModal}>Edit</DropdownItem>

                                            <DropdownItem tag={RdpLink} type="input1" display={display}>
                                                VNC Player 1
                                            </DropdownItem>

                                            <DropdownItem tag={RdpLink} type="input2" display={display}>
                                                VNC Player 2
                                            </DropdownItem>

                                            <DropdownItem tag={RdpLink} type="camera" display={display}>
                                                Camera IP
                                            </DropdownItem>

                                            <DropdownItem
                                                className={this.disabledClass}
                                                onClick={this.openTerminalModal}
                                            >
                                                Terminal
                                            </DropdownItem>

                                            <DropdownItem
                                                tag={FavoriteButton}
                                                display={this.props.display}
                                                showText
                                                onClick={this.favoriteDisplay}
                                                placement="right"
                                            />
                                        </DropdownMenu>
                                    </UncontrolledButtonDropdown>
                                </ButtonGroup>

                                <SoftwareUpdateAlert
                                    className="ps-2"
                                    updateStatus={display.displaySoftwareUpdateStatus}
                                />

                                <ServicesAlert onClick={this.openContractModal} className="ps-2 my-2" contract={this.contract} display={display} />
                            </div>

                            {account && (
                                <div>
                                    <small>Account: {account.accountName}</small>
                                </div>
                            )}
                        </div>

                        <div className="col-6 text-right">
                            <div className="h-100">
                                {this.hasLatAndLng && display.pollTime && (
                                    <div>
                                        <small>
                                            {displayFormatTime(
                                                display.pollTime,
                                                'M/d/yyyy h:mm a',
                                                display.serialNumber
                                            )}

                                            <span className="ps-1">({this.latLngOrigin})</span>
                                        </small>
                                    </div>
                                )}
                                {!this.hasLatAndLng && (
                                    <div>
                                        <small>*No Location Data*</small>
                                    </div>
                                )}

                                <a
                                    onClick={this.openDisplayEventsModal}
                                    className="cursor-pointer border-bottom-white ms-auto text-decoration-none"
                                >
                                    <small>
                                        <i className="text-white">{display.statusMessageBrief}</i>
                                    </small>
                                </a>
                            </div>
                        </div>

                        {this.renderModals && (
                            <>
                                <DisplayEventsModal
                                    display={display}
                                    modalHandle={this.displayEventsModalHandle}
                                    history={this.props.history}
                                />
                                <DisplayEditModal display={display} modalHandle={this.displayEditModalHandle} />
                                <TicketModal display={display} modalHandle={this.createTicketModalHandle} />
                                <TicketsModal
                                    display={display}
                                    modalHandle={this.ticketsModalHandle}
                                    history={this.props.history}
                                />
                                <TerminalModal
                                    modalHandle={this.terminalModalHandle}
                                    serialNumber={display.serialNumber}
                                />

                                {this.contract && (
                                    <ContractModal
                                        contract={this.contract}
                                        display={display}
                                        modalHandle={this.contractModalHandle}
                                        viewState="readOnly"
                                    />
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    @bind
    @action
    private openDisplayEventsModal(event: React.MouseEvent<HTMLAnchorElement>) {
        event.preventDefault();
        this.renderModals = true;

        this.displayEventsModalHandle.open();
    }

    @bind
    @action
    private openDisplayEditModal(event: React.MouseEvent<HTMLAnchorElement>) {
        event.preventDefault();
        this.renderModals = true;

        this.displayEditModalHandle.open();
    }

    @bind
    @action
    private openCreateTicketModal(event: React.MouseEvent<HTMLAnchorElement>) {
        event.preventDefault();
        this.renderModals = true;

        this.createTicketModalHandle.open();
    }

    @bind
    @action
    private openTerminalModal(event: React.MouseEvent<HTMLAnchorElement>) {
        event.preventDefault();
        this.renderModals = true;

        this.terminalModalHandle.open();
    }

    @bind
    @action
    private async favoriteDisplay() {
        await this.displaysStore.loadAsync(this.props.display.serialNumber, {
            params: {
                status: true,
                contract: true,
            },
        });
    }

    @computed
    private get contract() {
        return this.contractsStore.contracts.get(this.props.display.contract);
    }

    @computed
    private get disabledClass() {
        if (
            !this.props.display ||
            !this.props.display.displayConnectionData ||
            !this.props.display.displayConnectionData.displayBoardIP ||
            !this.props.display.displayConnectionData.nocServer
        ) {
            return 'disabled';
        } else return '';
    }

    @computed
    private get statusClassName() {
        const { display } = this.props;

        switch (display.videoState) {
            default:
                return 'dashboard-display-background-green';
            case 'showingVideo':
                return 'dashboard-display-background-green';
            case 'warning':
                return 'dashboard-display-background-warning';
            case 'noVideo':
                return 'dashboard-display-background-danger';
        }
    }

    @computed
    private get hasLatAndLng() {
        const { unitLocation, serialNumber } = this.props.display;
        if (!unitLocation || (!this.latitude && !this.longitude)) {
            return false;
        }

        return true;
    }

    @computed
    private get latLngOrigin() {
        const display = this.props.display;
        const gpsLat = display?.measurements?.find(s => s.id == Mri.WebApi.MeasurementId.GpsLatitude)?.floatValue;
        const configLat = display?.configPoll?.geoVuConfig?.latitude;
        return gpsLat ? 'GPS' : configLat ? 'Config' : 'SmartVu';
    }

    @computed
    private get latitude() {
        const display = this.props.display;
        const gpsLat = display?.measurements?.find(s => s.id == Mri.WebApi.MeasurementId.GpsLatitude)?.floatValue;
        const configLat = display?.configPoll?.geoVuConfig?.latitude;
        const smartVuLat = display?.unitLocation?.latitude;
        return gpsLat ? gpsLat : configLat ? configLat : smartVuLat;
    }

    @computed
    private get longitude() {
        const display = this.props.display;
        const gpsLong = display?.measurements?.find(s => s.id == Mri.WebApi.MeasurementId.GpsLongitude)?.floatValue;
        const configLong = display?.configPoll?.geoVuConfig?.longitude;
        const smartVuLng = display?.unitLocation?.longitude;

        return gpsLong ? gpsLong : configLong ? configLong : smartVuLng;
    }

    @bind
    @action
    private openContractModal() {
        this.renderModals = true;

        this.contractModalHandle.open();
    }

    private contractModalHandle = new ModalHandle();
    @observable
    private renderModals = false;
    @inject
    private accountStore!: AccountStore;
    @inject
    private appStore!: AppStore;
    @inject
    private displaysStore!: DisplaysStore;
    @inject
    private contractsStore!: ContractsStore;
    private displayEditModalHandle = new ModalHandle();
    private displayEventsModalHandle = new ModalHandle();
    private createTicketModalHandle = new ModalHandle();
    private ticketsModalHandle = new ModalHandle();
    private terminalModalHandle = new ModalHandle();
}
